import { useEffect, useState } from 'react';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import Utilities from '../../common/Utilities';
import AuthService from '../../services/AuthService';
import StripeService from '../../services/StripeService';
import { useNavigate } from 'react-router-dom';
import { trackInitiateCheckout, trackPurchase } from '../../utils/TrackingEvents';

type CheckOutProps = {
    userId: string;
    planId: string;
};

const CheckOut = (props: CheckOutProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState(true);
    const [error, setError] = useState<string | undefined>();
    const [success, setSuccess] = useState(false);
    const [plan, setPlan] = useState<any>(null);

    useEffect(() => {
      const getPlan = async () => {
        const response = await StripeService.getPlan(props.planId);
        setPlan(response.data);
        setLoadingPlan(false);
      }
      getPlan();
    }, [props.planId]);

    useEffect(() => {
      if (!plan) return;
      trackInitiateCheckout({
        value: plan.amount,
        currency: plan.currency,
        items: [{ item_id: props.planId, item_name: plan.name, price: plan.amount }],
      });
    }, [plan, props.planId]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        
        if (!stripe || !elements) {
            // Stripe aún no está cargado
            setError("Ha ocurrido un error. Inténtalo más tarde.");
            return;
        }

        // Validación de los elementos de Stripe
        const cardNumberElement = elements.getElement(CardNumberElement);
        const cardExpiryElement = elements.getElement(CardExpiryElement);
        const cardCvcElement = elements.getElement(CardCvcElement);
        
        if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
          setError("Ha ocurrido un error. Inténtalo más tarde.");
          return;
        }

        setLoading(true);
        setError("");
    
        const { token, error: stripeError } = await stripe.createToken(cardNumberElement);
    
        if (stripeError) {
          setLoading(false);
          setError(stripeError.message);
          return;
        }

        try {
            const response = await AuthService.createSuscripcionStripe(props.userId, { token });
            const { clientSecret, confirmed, transaction_id } = response.data;
            
            if (confirmed) {
              setLoading(false);
              setSuccess(true);
              trackPurchase({
                value: plan.amount,
                currency: plan.currency,
                transaction_id: transaction_id,
                items: [{ item_id: props.planId, item_name: plan.name, price: plan.amount }],
              });
              return;
            }
            
            // El PaymentIntent de la suscripción ya lleva la tarjeta: solo se completa la autenticación (si se pasara el
            // payment_method se crearía una segunda tarjeta idéntica en el cliente)
            const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
        
            if (error) {
              setError(error.message);
              setLoading(false);
              return;
            } else if (paymentIntent.status === 'succeeded') {
              setLoading(false);
              setSuccess(true);
              trackPurchase({
                value: plan.amount,
                currency: plan.currency,
                transaction_id: paymentIntent.id,
                items: [{ item_id: props.planId, item_name: plan.name, price: plan.amount }],
              });
              return;
            } else if (paymentIntent.status === 'requires_action') {
              setError('Se requiere autenticación adicional para completar el pago.');
              setLoading(false);
            }  else if (paymentIntent.status === 'requires_payment_method') {
              // Si el PaymentIntent requiere un método de pago válido
              setError('El método de pago proporcionado no es válido. Por favor, intente con otro.');
              setLoading(false);
            } else if (paymentIntent.status === 'canceled') {
              // Si el PaymentIntent fue cancelado
              setError('El pago fue cancelado. Por favor, intente nuevamente.');
              setLoading(false);
            } else {
              setError('Hubo un problema con el pago, por favor intente nuevamente.');
              setLoading(false);
            }

          } catch (error: any) {
            if (error.response?.status === 402) {
              // Cobro rechazado por el banco: no es una errata, se vacían los datos de la tarjeta
              cardNumberElement.clear();
              cardExpiryElement.clear();
              cardCvcElement.clear();
            }
            setError(error.response?.data?.message || "Error al procesar el pago. Por favor, intente nuevamente.");
            setLoading(false);
          }
    };

    const handleGoToLogin = () => {
      navigate('/login');
    };

    if (loadingPlan) {
      return <CircularProgress />;
    }

    if (!plan) {
        return (
          <Box sx={{ textAlign: 'center', margin: '2rem' }}>
            <Typography variant="h6" color="error">
              El plan seleccionado no es válido. Por favor, verifica tu selección.
            </Typography>
          </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '800px', md: '1200px' }, margin: '0 auto', padding: { xs: '1rem', sm: '2rem' } }}>
        {success ? 
        <Box
        sx={{
          backgroundColor: 'success.main',
          padding: 3,
          borderRadius: 2,
          boxShadow: 3,
          width: '100%',
          maxWidth: 600,
          margin: '0 auto',
          textAlign: 'center',
        }}
        >
          <Typography variant="h5" sx={{ marginBottom: 2, color: 'white' }}>
            ¡Suscripción activada con éxito!
          </Typography>

          <Typography variant="body1" sx={{ color: 'white' }}>
            Has iniciado el plan <strong>{plan.name}</strong>
          </Typography>
          <Typography variant="body1" sx={{ color: 'white' }}>
            <strong>{`${Utilities.formatAmount(plan.amount)} € / mes`}</strong> — Renovación automática mensual
          </Typography>

          <Button
            variant="contained"
            sx={{
              marginTop: 3,
              color: '#2e7d32',
              borderColor: '#2e7d32',
              padding: '12px 24px',
              fontSize: '16px',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: '#388e3c',
                backgroundColor: '#f0f0f0',
              },
            }}
            onClick={handleGoToLogin}
          >
            Gestiona tu clínica
          </Button>
        </Box> : 
        <Grid container spacing={4}>
          {/* Bloque Izquierdo - Detalles de la Compra */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 16 }}>
                {`Suscribirse a ${plan.name}`}
                </Typography>

                <Typography><span style={{ fontWeight: "bold", fontSize: "34px" }}>{`${Utilities.formatAmount(plan.amount)} €`}</span> por mes</Typography>

                <Box sx={{ marginBottom: 2, marginTop: 2 }}>
                    <Grid container>
                    <Grid item md={9} xs={9}>
                        <Typography variant="body1">
                            {plan.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Facturación mensual</Typography>
                    </Grid>
                    <Grid item md={3} xs={3}>
                        <Typography variant="body1">
                            {`${Utilities.formatAmount(plan.amount)} €`}
                        </Typography>
                    </Grid>
                    </Grid>
                </Box>
                <Divider />

                <Box sx={{ marginBottom: 2, marginTop: 2 }}>
                    <Grid container>
                    <Grid item md={9} xs={9}>
                        <Typography variant="body1">
                            Subtotal
                        </Typography>
                        <Typography variant="body1">
                            IVA
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={3}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {`${Utilities.formatAmount(plan.amount)} €`}
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            {`${Utilities.formatAmount(plan.tax)} €`}
                        </Typography>
                    </Grid>
                    </Grid>
                </Box>
                <Divider />

                <Box sx={{ marginBottom: 2, marginTop: 2 }}>
                    <Grid container>
                    <Grid item md={9} xs={9}>
                        <Typography variant="body1">
                            Total a pagar hoy
                        </Typography>
                    </Grid>
                    <Grid item md={3} xs={3}>
                        <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 'bold'}}>
                            {`${Utilities.formatAmount(plan.amount)} €`}
                        </Typography>
                    </Grid>
                    </Grid>
                </Box>
                  
              </CardContent>
            </Card>
          </Grid>

          {/* Bloque Derecho - Datos de Pago */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ maxWidth: 600, margin: "0 auto", paddingBottom: 2 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 16, paddingBottom: 2 }}>
                    Introduce los datos de tu tarjeta
                    </Typography>

                    <Grid container spacing={2}>
                      {/* Número de tarjeta */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: 2,
                            padding: "0.8rem",
                          }}
                        >
                          <CardNumberElement />
                        </Box>
                      </Grid>
                      
                      {/* Fecha de expiración y CVC */}
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: 2,
                            padding: "0.8rem",
                          }}
                        >
                          <CardExpiryElement />
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: 2,
                            padding: "0.8rem",
                          }}
                        >
                          <CardCvcElement />
                        </Box>
                      </Grid>

                    </Grid>
                </Box>
                              
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{ mb: 1.70 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Pagar y suscribirse'}
                </Button>

                <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: 12, mb: 1 }}>Al confirmar tu suscripción, permitirás que Zyra efectúe cargos de futuros pagos conforme a las condiciones estipuladas. Siempre puedes cancelar tu suscripción.</Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: 'text.secondary', fontSize: 10, textAlign: 'center' }}
                >
                  <a 
                    href="https://stripe.com/es/legal/consumer" 
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    Condiciones
                  </a> 
                  <span style={{ margin: '0 8px' }}>|</span>
                  <a 
                    href="https://stripe.com/es/privacy" 
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    Privacidad
                  </a>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>}
      </Box>
    );
};

export default CheckOut;
